<script lang="ts">
  import type { Atelier } from '../../stores/creation.svelte'

  let { atelier }: { atelier: Atelier } = $props()

  const regions = $derived(atelier.referentiel.regions)
  const capacites = $derived(atelier.referentiel.capacites)
  const region = $derived(regions.find((r) => r.id === atelier.feuille.region))
  /** Le livre n'offre qu'une entrée « au hasard » : il n'y a donc qu'une tirée. */
  const tiree = $derived(atelier.feuille.capacites.find((c) => c.d100 != null))
  const choisies = $derived(atelier.feuille.capacites.filter((c) => c.d100 == null))
  /** Toutes places prises : les entrées restantes ne sont plus proposables. */
  const complet = $derived(
    atelier.feuille.capacites.length >= (region?.a_retenir ?? 0),
  )

  let deDeRegion = $state<number | null>(null)

  function tirerRegion() {
    deDeRegion = atelier.tirerRegion()
  }

  /** Le nom d'affichage d'une Caractéristique, jamais son identifiant. */
  const nomDeCarac = (id: string) =>
    atelier.referentiel.caracteristiques.find((c) => c.id === id)?.nom ?? id
</script>

<p class="chapo">
  Le livre laisse le choix : lancer le dé, ou décider. La Région relève de 5 le
  plafond de deux Caractéristiques, et fait retenir deux Capacités communes.
</p>

<div class="tirage">
  <button type="button" class="de" onclick={tirerRegion}>Tirer au D100</button>
  {#if deDeRegion !== null}
    <span class="resultat">D100 = {deDeRegion}</span>
  {/if}
</div>

<div class="cartes">
  {#each regions as r (r.id)}
    <button
      type="button"
      class="carte"
      class:active={r.id === atelier.feuille.region}
      onclick={() => atelier.choisirRegion(r.id)}
    >
      <span class="plage">{r.min}–{r.max}</span>
      <span class="nom">{r.nom}</span>
      <span class="resume">{r.resume}</span>
      <span class="releve">
        +{r.releve_de} au plafond de {r.caracteristiques_relevees.map(nomDeCarac).join(' et ')}
      </span>
    </button>
  {/each}
</div>

{#if region}
  <h3>Capacités communes</h3>
  <p class="chapo">
    {region.a_retenir} à retenir dans la liste ci-dessous.
    {#if region.tirage_en_option}
      La dernière entrée n'est pas une Capacité mais une manière d'en obtenir
      une : elle occupe l'une des {region.a_retenir} places, elle ne s'y ajoute
      pas. Deux Capacités nommées font un compte juste — le dé ne s'impose
      jamais.
    {/if}
  </p>

  <div class="options">
    {#each region.options as id (id)}
      {@const prise = choisies.some((c) => c.capacite === id)}
      <button
        type="button"
        class="option"
        class:active={prise}
        disabled={!prise && complet}
        onclick={() => atelier.basculerCapacite(id)}
      >
        <span class="nom">{capacites[id]?.nom ?? id}</span>
        <span class="resume">{capacites[id]?.resume ?? ''}</span>
      </button>
    {/each}

    {#if region.tirage_en_option}
      {#if tiree}
        <div class="option active tiree">
          <span class="nom">{capacites[tiree.capacite]?.nom ?? tiree.capacite}</span>
          <span class="resume">{capacites[tiree.capacite]?.resume ?? ''}</span>
          <span class="marque">
            Tirée au hasard — D100 = <span class="data">{tiree.d100}</span>
          </span>
          <span class="actions">
            <button type="button" class="lien" onclick={() => atelier.relancerTirage()}>
              relancer
            </button>
            <button type="button" class="lien" onclick={() => atelier.retirerTirage()}>
              retirer
            </button>
          </span>
        </div>
      {:else}
        <button
          type="button"
          class="option au-hasard"
          disabled={complet}
          onclick={() => atelier.tirerUneCapacite()}
        >
          <span class="nom">Au hasard</span>
          <span class="resume">
            Une Capacité commune prise au Tableau des Capacités communes
            aléatoires. Le livre fait relancer si le dé donne une Capacité déjà
            retenue.
          </span>
          <span class="marque">Lancer le D100</span>
        </button>
      {/if}
    {/if}
  </div>

  {#if atelier.dernierTirage?.verdict === 'hors_table'}
    <p class="alerte">Le dé est tombé hors de la table : {atelier.dernierTirage.d100}.</p>
  {/if}
{/if}

<style>
  .chapo {
    color: var(--ink-soft);
    max-width: 62ch;
    margin: 0 0 calc(var(--pas) * 4);
  }
  h3 {
    font-family: 'Cinzel', serif;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: 0.85rem;
    margin: calc(var(--pas) * 8) 0 calc(var(--pas) * 2);
    color: var(--gold);
  }
  .cartes {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
    gap: calc(var(--pas) * 2);
  }
  .carte,
  .option {
    display: grid;
    gap: calc(var(--pas) * 0.5);
    text-align: left;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--r);
    padding: calc(var(--pas) * 3);
    color: var(--ink);
    cursor: pointer;
    font: inherit;
  }
  .carte:hover,
  .option:hover:not(:disabled) {
    border-color: var(--line-fort);
  }
  .carte.active,
  .option.active {
    border-color: var(--accent);
    box-shadow: inset 3px 0 0 var(--accent);
  }
  .option:disabled {
    opacity: 0.4;
    cursor: default;
  }
  .plage {
    font-size: 0.7rem;
    color: var(--ink-soft);
    font-family: system-ui, sans-serif;
    font-variant-numeric: tabular-nums;
  }
  .nom {
    font-family: 'Cinzel', serif;
    font-size: 1rem;
  }
  .resume {
    font-size: 0.85rem;
    color: var(--ink-soft);
  }
  .releve {
    text-transform: uppercase;
    font-size: 0.72rem;
    color: var(--gold);
    margin-top: var(--pas);
  }
  .options {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
    gap: calc(var(--pas) * 2);
  }
  .tirage {
    display: flex;
    align-items: center;
    gap: calc(var(--pas) * 3);
    margin-bottom: calc(var(--pas) * 4);
    flex-wrap: wrap;
  }
  .de {
    font-family: 'Cinzel', serif;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.8rem;
    font-weight: 700;
    background: var(--accent);
    color: var(--accent-txt);
    border: 1px solid var(--line-fort);
    border-radius: var(--r);
    min-height: 44px;
    padding: 0 calc(var(--pas) * 4);
    cursor: pointer;
  }
  .de:hover {
    filter: brightness(1.18);
  }
  .resultat {
    font-size: 0.85rem;
    color: var(--ink-soft);
  }
  .marque {
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.06em;
    color: var(--gold);
    margin-top: var(--pas);
  }
  .au-hasard {
    border-style: dashed;
  }
  .actions {
    display: flex;
    gap: calc(var(--pas) * 3);
    margin-top: var(--pas);
  }
  .lien {
    background: none;
    border: none;
    color: var(--acier);
    text-decoration: underline;
    cursor: pointer;
    font: inherit;
    font-size: 0.85rem;
  }
  .alerte {
    color: var(--ko);
    font-size: 0.9rem;
  }
</style>
